import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import YearBarChartContainer from "../../components/charts/yearBarChart/YearBarChartContainer";
import React, { useMemo } from "react";
import useDropdown from "../../hooks/useDropdown";
import RawTable from "../../components/RawTable";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const DEFAULT_SEARCH_PARAMS = { locationid: 79, parameterid: 29 };
const searchParamsSchema = z.object({
  locationid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.locationid),
  parameterid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.parameterid),
});

export default function YearComparison() {
  const { params, setParams } = useQueryParams(searchParamsSchema);

  const locOptions = useDropdown(
    "results-simple/download-dropdown-locs-active"
  );
  const parOptions = useDropdown(
    "results-simple/list-parameters-dropdown-simple-active"
  );

  const COLUMNS = [
    { title: "Date", field: "rdate", type: "date", defaultSort: "desc" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue_converted", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Depth", field: "depth_value", type: "numeric" },
    { title: "Flow Conditions", field: "flow_conditions_display" },
  ];

  const FILTERS_CONFIG = useMemo(
    () => [
      {
        type: "auto",
        param: "locationid",
        label: "Surface-water location",
        default: String(params.locationid),
        options: locOptions,
      },
      {
        type: "auto",
        param: "parameterid",
        label: "Parameter",
        default: String(params.parameterid),
        options: parOptions,
      },
    ],
    [locOptions, parOptions, params]
  );

  return (
    <>
      <Helmet title="Year Comparison" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Year Comparison
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This dashboard compares{" "}
            <strong>monthly average concentrations</strong> of a selected
            parameter across multiple calendar years for a single reservoir
            location.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Use this view to highlight <strong>seasonal patterns</strong>,
            evaluate <strong>interannual variability</strong>, or check for
            unusually high or low values in specific months over time.
          </Typography>
          <Typography variant="body1" gutterBottom>
            A <strong>depth filter</strong> is available to narrow the analysis
            to specific monitoring depths. When multiple depths are available,
            you can select one or more depths to include in the chart.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS_CONFIG} onChange={setParams} />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Graph
          </Typography>
          <YearBarChartContainer
            locationid={params.locationid}
            parameterid={params.parameterid}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>
          <Typography variant="body1" gutterBottom>
            This grouped bar chart compares{" "}
            <strong>average monthly concentrations</strong> across multiple
            years for a selected <strong>surface-water location</strong> and{" "}
            <strong>parameter</strong>. Each bar represents the average result
            for a given month and year.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Use the <strong>year selector chips</strong> below the graph to
            toggle which years are displayed. This makes it easy to visually
            compare <strong>seasonal trends</strong> or identify anomalies
            across different time periods.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Use the <strong>depth filter</strong> to limit the dataset to
            samples collected at specific depths. Only data from the selected
            depths will be shown in the chart.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Tooltip details show the precise{" "}
            <strong>monthly concentration</strong> for each selected year. Only
            months with valid data are shown in the chart and tooltip.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Values shown are calculated as <strong>monthly means</strong> from
            available field samples. Each year is represented by a distinct
            color for clarity.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>

          <RawTable
            endPoint="results-simple/year-comparison"
            params={params}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
